import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Mail, Lock, User, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export default function SetupPdg() {
  const navigate = useNavigate();
  const { user, setupPdg } = useAuth();
  const [check, setCheck] = useState(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/auth/setup-check")
      .then(({ data }) => setCheck(data.needs_setup))
      .catch(() => setCheck(false));
  }, []);

  if (user) return <Navigate to="/app/dashboard" replace />;
  if (check === false) return <Navigate to="/login" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) return setErr("Les mots de passe ne correspondent pas");
    if (pw.length < 8) return setErr("Mot de passe trop court (min 8 caractères)");
    setLoading(true);
    try {
      await setupPdg({ email: email.trim(), password: pw, full_name: fullName.trim() });
      navigate("/app/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-obsidian-950">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
        data-testid="setup-page"
      >
        <div className="card p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 grid place-items-center bg-citron rounded-sm">
              <Crown size={24} className="text-obsidian-950" />
            </div>
            <div>
              <div className="overline text-citron">Premier démarrage</div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">Création du compte PDG</h1>
            </div>
          </div>

          <p className="text-obsidian-300 text-sm leading-relaxed mb-6">
            Aucun compte administrateur suprême n'existe encore. Le compte PDG aura accès à toutes les entités, tous les modules et la gestion des utilisateurs.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="label block">Nom complet</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                <input
                  required
                  className="input pl-10"
                  placeholder="Archil RAND"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  data-testid="setup-name-input"
                />
              </div>
            </div>

            <div>
              <label className="label block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                <input
                  type="email"
                  required
                  className="input pl-10"
                  placeholder="archil@grouperops.ga"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="setup-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label block">Mot de passe</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                  <input
                    type="password"
                    required
                    className="input pl-10"
                    placeholder="min. 8 caractères"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    data-testid="setup-password-input"
                  />
                </div>
              </div>
              <div>
                <label className="label block">Confirmer</label>
                <input
                  type="password"
                  required
                  className="input"
                  placeholder="••••••••"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="setup-password2-input"
                />
              </div>
            </div>

            {err && (
              <div className="flex items-start gap-2 p-3 bg-bad/10 border border-bad/30 rounded-sm text-bad text-xs">
                <AlertCircle size={14} className="mt-0.5" /> <span>{err}</span>
              </div>
            )}

            <ul className="text-xs text-obsidian-400 space-y-1.5 py-2">
              <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-citron" /> Le compte aura accès aux 7 entités</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-citron" /> Mot de passe hashé (bcrypt) — jamais stocké en clair</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-citron" /> Toutes les actions sont enregistrées dans l'audit log</li>
            </ul>

            <button type="submit" disabled={loading} className="btn-primary w-full h-11 font-semibold" data-testid="setup-submit-button">
              {loading ? "Création…" : <>Créer le compte PDG <ArrowRight size={16} /></>}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
